import { Link } from 'react-router-dom';
import { movies } from '@/mocks/movies';
import { newsList } from '@/mocks/news';
import EmptyState from '@/components/base/EmptyState';
import MovieCard from '@/components/feature/MovieCard';
import { usePageMeta } from '@/hooks/usePageMeta';
import HeroCarousel from './components/HeroCarousel';
import CategoryShortcuts from './components/CategoryShortcuts';

export default function Home() {
  usePageMeta(
    '青苹果影院-海量影片在线观看与影视资讯平台',
    '青苹果影院为您提供丰富的电影和电视剧资源，涵盖最新影视动态和精彩剧情介绍，助您轻松查找和观看各类影视作品。',
    '青苹果影院,电影,电视剧,影视资源,影视资讯,影视平台,影片推荐',
  );

  const latestMovies = [...movies]
    .sort((a, b) => b.releaseDate.localeCompare(a.releaseDate))
    .slice(0, 12);

  const topRated = [...movies].sort((a, b) => b.rating - a.rating).slice(0, 5);

  const featuredNews = newsList[0];
  const newsPicks = newsList.slice(1, 5);

  return (
    <div className="w-full">
      <HeroCarousel />
      <div className="w-full max-w-[1440px] mx-auto">
        <CategoryShortcuts />

      <section className="w-full px-4 md:px-8 pt-10 md:pt-14">
        <div className="flex items-end justify-between gap-4 mb-4 md:mb-6">
          <div>
            <div className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-accent-500" aria-hidden="true" />
              <span className="text-xs tracking-[0.18em] text-foreground-500 uppercase font-label">Latest</span>
            </div>
            <h2 className="mt-2 text-xl md:text-2xl font-semibold text-foreground-950">最新影片推荐</h2>
            <p className="mt-1.5 text-xs md:text-sm text-foreground-500">
              按上线时间整理的新片与剧集，覆盖不同类型与地区
            </p>
          </div>
          <Link
            to="/category?sort=latest"
            className="inline-flex items-center gap-1 h-9 px-3.5 rounded-md border border-background-200 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-700 transition-colors whitespace-nowrap"
          >
            查看全部
            <i className="ri-arrow-right-line text-base" aria-hidden="true" />
          </Link>
        </div>

        {latestMovies.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-x-3 gap-y-6 md:gap-x-4 md:gap-y-8">
            {latestMovies.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>
        ) : (
          <EmptyState
            icon="ri-film-line"
            title="暂无推荐影片"
            description="新片还在整理中，可以先到分类页浏览全部影片。"
            actionLabel="浏览全部影片"
            actionTo="/category"
          />
        )}
      </section>

      <section className="w-full px-4 md:px-8 pt-12 md:pt-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
          <div className="lg:col-span-2 rounded-lg border border-background-200 bg-background-50 p-4 md:p-6">
            <div className="flex items-end justify-between gap-4">
              <div>
                <h2 className="text-lg md:text-xl font-semibold text-foreground-950">本周高分口碑榜</h2>
                <p className="mt-1 text-xs md:text-sm text-foreground-500">综合评分排序，适合慢慢看的作品</p>
              </div>
              <Link
                to="/category?sort=rating"
                className="text-xs md:text-sm text-primary-700 hover:text-primary-800 whitespace-nowrap"
              >
                完整榜单
              </Link>
            </div>

            <ul className="mt-4 md:mt-5 divide-y divide-background-200">
              {topRated.map((movie, index) => (
                <li key={movie.id}>
                  <Link
                    to={`/detail/${movie.id}`}
                    className="group flex items-center gap-3 md:gap-4 py-3 first:pt-0 last:pb-0"
                  >
                    <span className="w-7 text-center font-label text-xl md:text-2xl font-semibold text-background-400 group-hover:text-primary-400 transition-colors">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    <span className="w-11 h-14 md:w-12 md:h-16 shrink-0 rounded-md overflow-hidden bg-background-200">
                      <img
                        src={movie.poster}
                        alt={`${movie.title}海报`}
                        title={`${movie.title} - 影视详情`}
                        className="w-full h-full object-cover object-top"
                      />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium text-foreground-950 truncate group-hover:text-primary-700 transition-colors">
                        {movie.title}
                      </span>
                      <span className="mt-1 block text-[11px] md:text-xs text-foreground-500 truncate">
                        {movie.genres.join(' / ')} · {movie.region} · {movie.year}
                      </span>
                    </span>
                    <span className="shrink-0 flex items-center gap-1 text-accent-700 font-label text-sm font-semibold">
                      <i className="ri-star-fill text-xs" aria-hidden="true" />
                      {movie.rating.toFixed(1)}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="relative overflow-hidden rounded-lg min-h-[300px] md:min-h-[340px]">
            <img
              src="https://readdy.ai/api/search-image?query=cinematic%20moody%20portrait%20of%20an%20empty%20vintage%20cinema%20hall%20with%20warm%20aisle%20lights%2C%20deep%20teal%20curtains%20and%20rows%20of%20empty%20seats%2C%20soft%20dust%20in%20the%20projector%20beam%2C%20warm%20amber%20highlights%2C%20minimal%20elegant%20composition%2C%20high%20detail&width=800&height=900&seq=qa-home-editorial&orientation=portrait"
              alt="影院放映厅光影"
              title="青苹果影院编辑精选"
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/92 via-foreground-950/55 to-foreground-950/20" />
            <div className="relative z-10 h-full flex flex-col justify-end p-5 md:p-6">
              <span className="inline-flex w-fit items-center gap-1.5 px-2 py-1 rounded-md bg-accent-500 text-foreground-950 text-[11px] font-medium">
                <i className="ri-quill-pen-line text-xs" aria-hidden="true" />
                编辑手记
              </span>
              <h3 className="mt-3 text-lg md:text-xl font-semibold text-background-50 leading-snug">
                好故事不急着给答案
              </h3>
              <p className="mt-2 text-xs md:text-sm text-background-100/75 leading-relaxed">
                我们整理了近期值得一读的观察与幕后记录，聊聊那些让影视作品变得耐看的细节。
              </p>
              <Link
                to="/news"
                className="mt-4 inline-flex items-center gap-1.5 text-sm text-background-50 font-medium hover:text-accent-300 transition-colors w-fit"
              >
                阅读影视资讯
                <i className="ri-arrow-right-line" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 pt-12 md:pt-16">
        <div className="flex items-end justify-between gap-4 mb-4 md:mb-6">
          <div>
            <div className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-accent-500" aria-hidden="true" />
              <span className="text-xs tracking-[0.18em] text-foreground-500 uppercase font-label">Insights</span>
            </div>
            <h2 className="mt-2 text-xl md:text-2xl font-semibold text-foreground-950">影视资讯精选</h2>
          </div>
          <Link
            to="/news"
            className="inline-flex items-center gap-1 h-9 px-3.5 rounded-md border border-background-200 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-700 transition-colors whitespace-nowrap"
          >
            全部资讯
            <i className="ri-arrow-right-line text-base" aria-hidden="true" />
          </Link>
        </div>

        {featuredNews ? (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-6">
            <Link
              to={`/news/${featuredNews.id}`}
              className="group lg:col-span-5 rounded-lg overflow-hidden border border-background-200 bg-background-50"
            >
              <div className="relative w-full aspect-[16/10] bg-background-200 overflow-hidden">
                <img
                  src={featuredNews.cover}
                  alt={featuredNews.title}
                  title={`${featuredNews.title} - 影视资讯`}
                  className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.03]"
                />
                <span className="absolute top-3 left-3 px-2 py-0.5 rounded-md bg-background-50/90 text-[11px] text-primary-700 font-medium">
                  头条
                </span>
              </div>
              <div className="p-4 md:p-5">
                <span className="text-[11px] text-foreground-500 font-label">{featuredNews.date}</span>
                <h3 className="mt-2 text-base md:text-lg font-semibold text-foreground-950 leading-snug group-hover:text-primary-700 transition-colors">
                  {featuredNews.title}
                </h3>
                <p className="mt-2 text-xs md:text-sm text-foreground-600 leading-relaxed line-clamp-2">
                  {featuredNews.summary}
                </p>
              </div>
            </Link>

            <div className="lg:col-span-7 rounded-lg border border-background-200 bg-background-50 px-4 md:px-6">
              <ul className="divide-y divide-background-200">
                {newsPicks.map((item) => (
                  <li key={item.id}>
                    <Link to={`/news/${item.id}`} className="group flex items-start gap-4 py-4 md:py-5">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 text-[11px] text-foreground-500">
                          <span className="px-1.5 py-0.5 rounded bg-secondary-100 text-secondary-800">资讯</span>
                          <span className="font-label">{item.date}</span>
                        </div>
                        <h3 className="mt-2 text-sm md:text-base font-medium text-foreground-950 leading-snug group-hover:text-primary-700 transition-colors">
                          {item.title}
                        </h3>
                        <p className="mt-1.5 text-xs md:text-sm text-foreground-600 leading-relaxed line-clamp-1">
                          {item.summary}
                        </p>
                      </div>
                      <span className="hidden sm:block w-20 h-14 shrink-0 rounded-md overflow-hidden bg-background-200">
                        <img
                          src={item.cover}
                          alt={item.title}
                          title={`${item.title} - 影视资讯`}
                          className="w-full h-full object-cover object-top"
                        />
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ) : (
          <EmptyState
            icon="ri-newspaper-line"
            title="暂无最新资讯"
            description="资讯内容正在整理中，稍后再来看看。"
            actionLabel="返回首页"
            actionTo="/"
          />
        )}
      </section>

      <section className="w-full px-4 md:px-8 pt-14 md:pt-20 pb-14 md:pb-20">
        <div className="relative overflow-hidden rounded-lg bg-primary-900 px-6 py-10 md:px-12 md:py-12">
          <div className="absolute -right-16 -top-16 w-64 h-64 rounded-full bg-primary-800/60" aria-hidden="true" />
          <div className="absolute -bottom-20 right-24 w-48 h-48 rounded-full bg-primary-800/40" aria-hidden="true" />
          <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="max-w-xl">
              <span className="text-xs tracking-[0.2em] text-accent-300 uppercase font-label">Start Exploring</span>
              <h2 className="mt-3 text-2xl md:text-3xl font-semibold text-background-50 leading-tight">
                今晚看点什么？按你的心情挑一部
              </h2>
              <p className="mt-3 text-sm text-background-100/70 leading-relaxed">
                从类型、地区到年份，分类页支持多条件组合筛选与排序，几秒钟就能找到想看的那一部。
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <Link
                to="/category"
                className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-accent-500 text-foreground-950 text-sm font-medium hover:bg-accent-400 transition-colors whitespace-nowrap"
              >
                <i className="ri-equalizer-line text-base" aria-hidden="true" />
                开始筛选影片
              </Link>
              <Link
                to="/search?q=星"
                className="inline-flex items-center gap-2 h-11 px-5 rounded-md border border-background-50/30 text-background-50 text-sm font-medium hover:bg-background-50/10 transition-colors whitespace-nowrap"
              >
                试着搜索
              </Link>
            </div>
          </div>
        </div>
      </section>
      </div>
    </div>
  );
}